import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { LoginResponse, ProblemDetails } from '@bhops/shared';
import { syncAuthFromStorage, useAuthStore } from '@/stores/auth';

// Centralized axios instance. All API modules import from here.
// Access token lives in Zustand (memory); refresh token is in localStorage (shared by all tabs).

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  withCredentials: false,
  timeout: 15_000,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// ----- Refresh token rotation -------------------------------------------
// Inspired by axios-auth-refresh: when a request 401s, we pause new requests,
// attempt a /auth/refresh, then replay the queue. Refresh itself must not be
// retried (prevents loops).

let refreshInFlight: Promise<LoginResponse> | null = null;

const REFRESH_LOCK = 'bhops-auth-refresh';

/**
 * 탭 사이 갱신 잠금(Web Locks). 모든 탭이 갱신 토큰 하나를 같이 쓰므로(stores/auth.ts), 두 탭이
 * 같은 토큰으로 동시에 갱신하면 늦은 쪽이 '이미 바뀐 토큰'을 내밀어 서버가 갈래 전체를 끊는다.
 * 잠금이 없는 환경(시험·옛 브라우저)에서는 그냥 한다.
 */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  return locks ? (locks.request(REFRESH_LOCK, fn) as Promise<T>) : fn();
}

export async function refreshTokens(): Promise<LoginResponse> {
  return withRefreshLock(async () => {
    // 잠금을 기다리는 사이 다른 탭이 갱신했을 수 있다 — 메모리 말고 저장소의 최신 토큰을 쓴다.
    await syncAuthFromStorage();
    const rt = useAuthStore.getState().refreshToken;
    if (!rt) throw new NoRefreshTokenError();

    const response = await axios.post<LoginResponse>(
      `${baseURL}/auth/refresh`,
      { refreshToken: rt },
      { timeout: 15_000 },
    );
    // 잠금을 풀기 전에 저장소에 새 토큰을 적는다(persist 는 동기 저장).
    useAuthStore.getState().setSession(response.data);
    return response.data;
  });
}

class NoRefreshTokenError extends Error {
  constructor() {
    super('No refresh token available');
  }
}

/**
 * 갱신 실패 중 **로그인이 정말 끝난 경우만** 지운다(토큰 없음 · 서버가 401/403). 네트워크가
 * 잠깐 끊긴 것까지 지우면, 저장소가 탭 공용이라 모든 탭이 함께 로그아웃된다.
 */
function sessionIsDead(err: unknown): boolean {
  if (err instanceof NoRefreshTokenError) return true;
  const status = axios.isAxiosError(err) ? err.response?.status : undefined;
  return status === 401 || status === 403;
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<ProblemDetails>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const status = error.response?.status;
    const isRefreshCall = original?.url?.includes('/auth/refresh') ?? false;

    if (status === 401 && !original._retried && !isRefreshCall) {
      original._retried = true;
      try {
        refreshInFlight ??= refreshTokens().finally(() => {
          refreshInFlight = null;
        });
        const fresh = await refreshInFlight;
        original.headers.set('Authorization', `Bearer ${fresh.accessToken}`);
        return apiClient.request(original);
      } catch (refreshErr) {
        if (sessionIsDead(refreshErr)) useAuthStore.getState().clear();
        throw refreshErr;
      }
    }

    throw error;
  },
);

/**
 * Flatten an API error into a single human-readable string.
 *
 * Preference order when the server returned RFC 7807 Problem+JSON:
 *   1. Validation errors list (one per line) — most actionable
 *   2. `detail` — specific explanation for this occurrence
 *   3. `title` — generic category fallback
 *   4. axios / network error message
 *
 * docs/work_log/debug-api-500-recurring §방지책 #3 — network 실패 (API
 * 서버 다운, Vite proxy 가 connection-refused 를 5xx 로 변환) 케이스를
 * **명시적 한글 메시지**로 표면화. 이전엔 "Request failed with status
 * code 500" 만 떠 운영자가 frontend 코드 문제로 오인.
 *
 * 분류:
 *   - err.response 없음 + ERR_NETWORK / ECONNREFUSED → 진짜 network down
 *   - err.code === 'ECONNABORTED' → 클라이언트 timeout (15s)
 *   - status 502/503/504 → upstream gateway 문제 (Vite proxy → API down 포함)
 *   - status 500 + body 없음 → 위와 같은 케이스이거나 API 가 진짜 500
 *   - 그 외 → 기존 ProblemDetails 우선순위
 */
export function extractApiError(err: unknown): string {
  if (axios.isAxiosError<ProblemDetails>(err)) {
    // ── network-layer 실패 (response 없음) ────────────────────────
    if (!err.response) {
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        return '서버가 응답하지 않습니다. 잠시 후 다시 시도해 주세요.';
      }
      if (
        err.code === 'ERR_NETWORK' ||
        err.code === 'ECONNREFUSED' ||
        err.message?.toLowerCase().includes('network')
      ) {
        return '서버에 연결할 수 없습니다. 인터넷 연결을 확인하고, 계속되면 관리자에게 알려 주세요.';
      }
    }

    // ── upstream gateway 에러 (Vite proxy 가 502/503/504 변환) ───
    const status = err.response?.status;
    if (status === 502 || status === 503 || status === 504) {
      return '서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.';
    }

    const body = err.response?.data;
    if (body) {
      if (body.errors && body.errors.length > 0) {
        return body.errors.map((e) => e.detail).join('\n');
      }
      if (body.detail) return body.detail;
      if (body.title) return body.title;
    }

    // ── status 500 with no body — Vite proxy 가 connection-refused 를
    //    bare 500 으로 변환한 케이스 (API 가 진짜 500 을 응답할 때는
    //    ProblemDetails body 가 동봉됨). frontend 코드 문제 오인을 막기 위해
    //    network-layer 메시지로 fallback.
    if (status === 500 && !body) {
      return '서버가 응답하지 않습니다. 잠시 후 다시 시도하고, 계속되면 관리자에게 알려 주세요.';
    }
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}
